import { z } from 'zod';

import { isWholeIn, optionalWholeIn, wholeIn } from './fields';

const CONTINUE_URL = /^https:\/\/[^\s]*\{token\}/;

export const behaviourSchema = z.object({
  behaviour: z.object({
    showTypingToStaff: z.boolean(),
    soundEnabled: z.boolean(),
    allowUploads: z.boolean(),
    allowRating: z.boolean(),
    allowTranscript: z.boolean(),
    suggestedRepliesEnabled: z.boolean(),
    idleCloseMinutes: wholeIn(5, 1440, 'chatSettings:validation.idleClose'),
    missedAfterSeconds: wholeIn(30, 3600, 'chatSettings:validation.missedAfter'),
    autoCloseResolvedHours: wholeIn(1, 720, 'chatSettings:validation.autoClose'),
    discountCapPerAgentPerDay: wholeIn(0, 1000, 'chatSettings:validation.discountCap'),
    triggerDiscountCapPerDay: wholeIn(0, 10_000, 'chatSettings:validation.triggerDiscountCap'),
    freeSizerOffer: z.boolean(),
  }),
  emails: z
    .object({
      replyWhenAway: z.boolean(),
      offlineAck: z.boolean(),
      autoTranscript: z.boolean(),
      autoTranscriptAfterMinutes: z.string().trim(),
      /** Empty uses https://<primary domain>/chat/c/{token}. */
      continueUrlTemplate: z
        .string()
        .trim()
        .max(500, { error: 'chatSettings:validation.continueUrl' })
        .refine((v) => v === '' || CONTINUE_URL.test(v), {
          error: 'chatSettings:validation.continueUrl',
        }),
    })
    .superRefine((value, ctx) => {
      if (value.autoTranscript && !isWholeIn(value.autoTranscriptAfterMinutes, 5, 1440)) {
        ctx.addIssue({
          code: 'custom',
          path: ['autoTranscriptAfterMinutes'],
          message: 'chatSettings:validation.transcriptDelay',
        });
      }
    }),
  retention: z
    .object({
      purge: z.boolean(),
      purgeClosedAfterMonths: z.string().trim(),
    })
    .superRefine((value, ctx) => {
      if (value.purge && !isWholeIn(value.purgeClosedAfterMonths, 1, 120)) {
        ctx.addIssue({
          code: 'custom',
          path: ['purgeClosedAfterMonths'],
          message: 'chatSettings:validation.retentionMonths',
        });
      }
    }),
  sla: z.object({
    /** Empty means no target. */
    firstResponseSeconds: optionalWholeIn(30, 604_800, 'chatSettings:validation.firstResponse'),
    resolutionHours: optionalWholeIn(1, 2160, 'chatSettings:validation.resolution'),
  }),
  attribution: z.object({
    windowDays: wholeIn(1, 90, 'chatSettings:validation.attribution'),
  }),
  help: z.object({
    widgetSearch: z.boolean(),
    suggestArticles: z.boolean(),
  }),
});

export type BehaviourForm = z.infer<typeof behaviourSchema>;
